import { Question, ItemValue } from "survey-core";
import { createStyles } from "antd-style";
import React, { useCallback, useMemo, useRef } from "react";
import { defaultDamageFromDirectionSVGPaths } from "../constants/defaultPaths";

const DEFAULT_IMAGE_HEIGHT_PX = "569";
const DEFAULT_IMAGE_WIDTH_PX = "297";
const DEFAULT_SVG_WIDTH_PX = "200";

const GRID_COLS = 3;
const GRID_ROWS = 3;

// biome-ignore format: kept as a visual 3x3 grid (matches the rendered layout).
const TAB_ORDER: readonly string[] = [
  "front_left", "front", "front_right",
  "left", "roof", "right",
  "rear_left", "rear", "rear_right",
];

const sortByTabOrder = <T extends { side: string }>(paths: T[]): T[] => {
  const indexOf = (side: string) => {
    const i = TAB_ORDER.indexOf(side);
    return i === -1 ? TAB_ORDER.length : i;
  };
  return [...paths].sort((a, b) => indexOf(a.side) - indexOf(b.side));
};

type ArrowKey = "ArrowUp" | "ArrowDown" | "ArrowLeft" | "ArrowRight";

const ARROW_DELTAS: Record<ArrowKey, { row: number; col: number }> = {
  ArrowUp: { row: -1, col: 0 },
  ArrowDown: { row: 1, col: 0 },
  ArrowLeft: { row: 0, col: -1 },
  ArrowRight: { row: 0, col: 1 },
};

const isArrowKey = (key: string): key is ArrowKey => key in ARROW_DELTAS;

// Returns the next side in the 3x3 grid for the given arrow key, or null when
// the move would go past an edge (no wrap-around).
const getNextSide = (currentSide: string, key: ArrowKey): string | null => {
  const idx = TAB_ORDER.indexOf(currentSide);
  if (idx === -1) return null;
  const { row: dRow, col: dCol } = ARROW_DELTAS[key];
  const nextRow = Math.floor(idx / GRID_COLS) + dRow;
  const nextCol = (idx % GRID_COLS) + dCol;
  const outOfBounds =
    nextRow < 0 || nextRow >= GRID_ROWS || nextCol < 0 || nextCol >= GRID_COLS;
  return outOfBounds
    ? null
    : (TAB_ORDER[nextRow * GRID_COLS + nextCol] ?? null);
};

interface DirectionPath extends React.SVGProps<SVGPathElement> {
  side: string;
}

export class DamageFromDirectionQuestionModel extends Question {
  imageStyle?: React.CSSProperties;
  imageUrl?: string;
  directionPaths?: DirectionPath[] = [];

  constructor(name: string) {
    super(name);
    this.createItemValues("choices");
  }

  getType() {
    return "damage-from-direction";
  }

  get choices(): ItemValue[] {
    return this.getPropertyValue("choices");
  }
  set choices(val: ItemValue[]) {
    this.setPropertyValue("choices", val);
  }
}

interface DamageFromDirectionQuestionProps {
  question: DamageFromDirectionQuestionModel;
}

const useStyles = createStyles(({ css, token }) => ({
  svgPath: css`
    fill: transparent;
    opacity: 0.8;
    cursor: pointer;
    transition: all 0.3s ease;
    &:hover {
      fill: ${token.colorPrimary};
      opacity: 1;
    }
    &:focus {
      outline: none;
      fill: ${token.colorPrimary};
      opacity: 1;
      stroke: ${token.colorPrimary};
      stroke-width: 2;
    }
    &:focus-visible {
      outline: none;
      stroke: ${token.colorPrimary};
      stroke-width: 2;
    }
  `,
  svgPathActive: css`
    fill: ${token.colorPrimary};
    opacity: 0.9;
    cursor: pointer;
    transition: all 0.3s ease;
    &:hover {
      opacity: 1;
    }
    &:focus {
      outline: none;
      stroke: ${token.colorPrimary};
      stroke-width: 2;
    }
    &:focus-visible {
      outline: none;
      stroke: ${token.colorPrimary};
      stroke-width: 2;
    }
  `,
  pillButton: css`
    background-color: transparent;
    color: ${token.colorText};
    border: 1px solid ${token.colorBorder};
    border-radius: 16px;
    padding: 6px 16px;
    cursor: pointer;
    font-size: 14px;
    line-height: 1.5;
    transition: all 0.3s ease;
    &:hover {
      border-color: ${token.colorPrimary};
      color: ${token.colorPrimary};
    }
  `,
  pillButtonActive: css`
    background-color: ${token.colorPrimary};
    color: ${token.colorWhite};
    border: 1px solid ${token.colorPrimary};
    border-radius: 16px;
    padding: 6px 16px;
    cursor: pointer;
    font-size: 14px;
    line-height: 1.5;
    transition: all 0.3s ease;
  `,
  buttonContainer: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 12px;
  `,
}));

export const DamageFromDirectionQuestion = ({
  question,
}: DamageFromDirectionQuestionProps) => {
  const { styles } = useStyles();
  const { directionPaths, imageUrl, imageStyle, name } = question;

  const allPaths = useMemo(
    () =>
      sortByTabOrder(
        directionPaths && directionPaths.length > 1
          ? directionPaths
          : defaultDamageFromDirectionSVGPaths,
      ),
    [directionPaths],
  );

  const imageSrc = imageUrl
    ? imageUrl
    : "https://cdn.rumbledirect.com/static/widget/form/car-side-selector.png";

  const svgPathSides = useMemo(
    () => new Set(allPaths.map((p) => p.side)),
    [allPaths],
  );

  // Choices whose value doesn't match any SVG path → rendered as buttons.
  const nonSvgChoices = useMemo(() => {
    if (!question.choices || question.choices.length === 0) return [];
    return question.choices.filter(
      (c: ItemValue) => !svgPathSides.has(c.value),
    );
  }, [question.choices, svgPathSides]);

  // Map of side → localized label, derived from question.choices.
  const sideLabels = useMemo(() => {
    const map = new Map<string, string>();
    if (question.choices) {
      for (const c of question.choices as ItemValue[]) {
        if (svgPathSides.has(c.value)) {
          map.set(c.value, c.text ?? c.value);
        }
      }
    }
    return map;
  }, [question.choices, svgPathSides]);

  const handleClick = (value: string) => {
    question.value = value;
  };

  const isDefaultLayout = useMemo(
    () =>
      allPaths.length === TAB_ORDER.length &&
      allPaths.every((p) => TAB_ORDER.includes(p.side)),
    [allPaths],
  );

  const pathRefs = useRef<Map<string, SVGPathElement>>(new Map());
  const registerPathRef = useCallback(
    (side: string, node: SVGPathElement | null) => {
      if (node) {
        pathRefs.current.set(side, node);
      } else {
        pathRefs.current.delete(side);
      }
    },
    [],
  );

  const handleSvgKeyDown = (event: React.KeyboardEvent<SVGSVGElement>) => {
    if (!isDefaultLayout) return;
    if (!isArrowKey(event.key)) return;
    const target = event.target as Element | null;
    const currentSide = target?.getAttribute?.("data-side");
    if (!currentSide) return;
    const nextSide = getNextSide(currentSide, event.key);
    if (!nextSide) {
      // At a grid edge: swallow the event so the page doesn't scroll while the
      // user is still navigating panels.
      event.preventDefault();
      return;
    }
    const nextNode = pathRefs.current.get(nextSide);
    if (!nextNode) return;
    event.preventDefault();
    nextNode.focus();
  };

  return (
    <div style={{ textAlign: "center", maxWidth: "340px", margin: "0 auto" }}>
      <svg
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${DEFAULT_IMAGE_WIDTH_PX} ${DEFAULT_IMAGE_HEIGHT_PX}`}
        style={{ width: DEFAULT_SVG_WIDTH_PX }}
        onKeyDown={handleSvgKeyDown}
      >
        <g role="radiogroup" aria-label={question.title ?? name}>
          <use xlinkHref={`#${name}`} x="0" y="0" />

          {allPaths.map(({ side, d }) => (
            <DirectionSVGPath
              key={side}
              side={side}
              label={sideLabels.get(side) ?? side}
              isActive={question.value === side}
              d={d}
              onClick={() => handleClick(side)}
              registerRef={registerPathRef}
            />
          ))}
        </g>

        <defs>
          <image
            id={name}
            // TODO: Add support for CDN config in Survey package
            xlinkHref={imageSrc}
            style={{
              width: DEFAULT_IMAGE_WIDTH_PX,
              height: DEFAULT_IMAGE_HEIGHT_PX,
              ...imageStyle,
            }}
          />
        </defs>
      </svg>

      {nonSvgChoices.length > 0 && (
        <div className={styles.buttonContainer}>
          <span style={{ fontSize: "14px" }}>Of anders:</span>
          {nonSvgChoices.map((choice: ItemValue) => (
            <DirectionOptionButton
              key={choice.value}
              label={choice.text}
              isActive={question.value === choice.value}
              onClick={() => handleClick(choice.value)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface DirectionSVGPathProps {
  side: string;
  label: string;
  isActive: boolean;
  d?: string;
  onClick: () => void;
  registerRef: (side: string, node: SVGPathElement | null) => void;
}

const DirectionSVGPath = ({
  side,
  label,
  d,
  isActive,
  onClick,
  registerRef,
}: DirectionSVGPathProps) => {
  const { styles, cx } = useStyles();
  const handleKeyDown = (event: React.KeyboardEvent<SVGPathElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };
  return (
    <path
      ref={(node) => registerRef(side, node)}
      d={d}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="radio"
      aria-label={label}
      aria-checked={isActive}
      data-side={side}
      className={cx(isActive ? styles.svgPathActive : styles.svgPath)}
    />
  );
};

interface DirectionOptionButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const DirectionOptionButton = ({
  label,
  isActive,
  onClick,
}: DirectionOptionButtonProps) => {
  const { styles, cx } = useStyles();
  return (
    <button
      type="button"
      aria-pressed={isActive}
      onClick={onClick}
      className={cx(isActive ? styles.pillButtonActive : styles.pillButton)}
    >
      {label}
    </button>
  );
};
